import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { EMPTY, catchError, tap } from 'rxjs';
import { userStorage } from 'src/app/constants/storage-constants';
import { UserDto } from 'src/app/generated/backend/models';
import { LoginService } from 'src/app/generated/backend/services';
import { environment } from 'src/environments/environment';
import { ResetBookingAction } from '../booking/booking.actions';
import { ResetClientDetailsAction } from '../client-details/client-details.actions';
import { ResetClientsAction } from '../clients/clients.actions';
import { ResetCollectedServicesAction } from '../collected-services/collected-services.actions';
import { BackToAuthAction, LoginUserAction, LogoutUserAction, VerifyTwoFactorAction } from './user.actions';
import { AuthStage, UserStateModel } from './user.state-model';

const defaults: UserStateModel = {
  loading: false,
  error: false,
  user: null,
  authStage: AuthStage.Anonymous,
};

@State<UserStateModel>({
  name: 'user',
  defaults,
})
@Injectable()
export class UserState {
  constructor(private loginService: LoginService) {}

  ngxsOnInit(ctx: StateContext<UserStateModel>) {
    this.loadLogin(ctx);
  }

  private persistAuth(ctx: StateContext<UserStateModel>) {
    const state = ctx.getState();
    if (state.user) {
      localStorage.setItem(
        userStorage,
        JSON.stringify({ user: state.user, authStage: state.authStage })
      );
    } else {
      localStorage.removeItem(userStorage);
    }
  }

  private saveLogin(ctx: StateContext<UserStateModel>, login: UserDto | null) {
    ctx.patchState({ user: login });
    this.persistAuth(ctx);
  }

  private loadLogin(ctx: StateContext<UserStateModel>) {
    try {
      const loginString = localStorage.getItem(userStorage);
      if (loginString) {
        const parsed = JSON.parse(loginString);
        if (parsed && typeof parsed === 'object' && 'user' in parsed) {
          ctx.patchState({
            user: parsed.user as UserDto,
            authStage: (parsed.authStage as AuthStage) ?? AuthStage.Authenticated,
          });
        } else {
          // Backward compatibility: old format was just the user object
          ctx.patchState({ user: parsed as UserDto, authStage: AuthStage.Authenticated });
        }
      }
    } catch (error) {
      this.saveLogin(ctx, null);
      if (!environment.production) {
        console.error(error);
      }
    }
  }

  @Action(LoginUserAction)
  login(ctx: StateContext<UserStateModel>, { name, password }: LoginUserAction) {
    // Start login with credentials only, no token expected at this step
    ctx.patchState({ loading: true, error: false, authStage: AuthStage.Password });
    return this.loginService
      .postLoginData$Json({
        body: { name, password, code2FA: '' },
      })
      .pipe(
        tap(() => {
          // Do NOT save user yet (no token provided). Move to 2FA step.
          ctx.patchState({
            loading: false,
            error: false,
            authStage: AuthStage.TwoFactor,
            pendingName: name,
            pendingPassword: password,
          });
          // No persist here since user is null; persistAuth only stores when user exists
        }),
        catchError(error => {
          console.error(error);
          // On error, clear any stored login and pending credentials
          this.saveLogin(ctx, null);
          ctx.patchState({
            loading: false,
            error: true,
            authStage: AuthStage.Anonymous,
            pendingName: null,
            pendingPassword: null,
          });
          return EMPTY;
        }),
      );
  }

  @Action(VerifyTwoFactorAction)
  verifyTwoFactor(ctx: StateContext<UserStateModel>, { code }: VerifyTwoFactorAction) {
    const state = ctx.getState();
    const name = state.pendingName ?? '';
    const password = state.pendingPassword ?? '';
    if (!name || !password) {
      // If for some reason we lost credentials, force back to auth
      ctx.patchState({ loading: false, error: true, authStage: AuthStage.Password });
      return EMPTY;
    }
    ctx.patchState({ loading: true, error: false });
    return this.loginService
      .postLoginData$Json({
        body: { name, password, code2FA: code },
      })
      .pipe(
        tap(response => {
          // Now we expect a token (UserDto) in the response
          this.saveLogin(ctx, response);
          ctx.patchState({
            loading: false,
            error: false,
            authStage: AuthStage.Authenticated,
            pendingName: null,
            pendingPassword: null,
          });
          this.persistAuth(ctx);
        }),
        catchError(error => {
          console.error(error);
          // On error, keep asking for 2FA, keep pending credentials
          ctx.patchState({
            loading: false,
            error: true,
            authStage: AuthStage.TwoFactor,
          });
          return EMPTY;
        }),
      );
  }

  @Action(LogoutUserAction)
  logout(ctx: StateContext<UserStateModel>) {
    this.saveLogin(ctx, null);
    ctx.patchState({ ...defaults, pendingName: null, pendingPassword: null });
    return ctx.dispatch([
      ResetCollectedServicesAction,
      ResetClientsAction,
      ResetClientDetailsAction,
      ResetBookingAction,
    ]);
  }

  @Action(BackToAuthAction)
  backToAuth(ctx: StateContext<UserStateModel>) {
    ctx.patchState({ loading: false, error: false, authStage: AuthStage.Password });
    this.persistAuth(ctx);
  }
}
